import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';

import { FormattedMessage } from '../../../../../components/Intl/withIntl.js';
import { getContentBlockItemValue, getContentBlockItemsByName, getContentBlockMixedItem } from '../../../CmsActions.js';

import HtmlComponent from '../../../../../components/Text/HtmlComponent.js';
import PredictiveLink from '../../view/PredictiveLink.js';

// Fixed row labels, translated via FormattedMessage (values come from the panel document).
const TIER_ROWS = [
    { field: 'minimum', id: 'pricingTierMinimum', defaultMessage: 'Minimum order' },
    { field: 'franco', id: 'pricingTierFreeShipping', defaultMessage: 'Free shipping from' },
    { field: 'packaging', id: 'pricingTierPackaging', defaultMessage: 'Packaging' },
    { field: 'idealFor', id: 'pricingTierIdealFor', defaultMessage: 'Ideal for' },
];

// Comparative presentation of the professional tiers (e.g. Mini-Pro / Semi-Pro / PRO): one card per
// tier, aligned rows for easy comparison and a highlighted "featured" tier. Fully CMS-driven — row
// labels and every value come from the panel document, so it stays reusable and per-language.
function PanelPricingTiers({ panel }) {
    const eyebrow = getContentBlockItemValue(panel.blocks, 'eyebrow');
    const title = getContentBlockItemValue(panel.blocks, 'title');
    const intro = getContentBlockItemValue(panel.blocks, 'intro');
    const footnote = getContentBlockItemValue(panel.blocks, 'footnote');

    const tiers = getContentBlockItemsByName(panel.blocks, 'tiers');
    if(!tiers.length) {
        return null;
    }

    const tierValue = (tier, name) => (getContentBlockMixedItem(tier, name) || {}).text;
    const tierRows = tier => TIER_ROWS
        .map(row => ({ ...row, value: tierValue(tier, row.field) }))
        .filter(row => row.value);

    return (
        <Container className="py-3">
            {eyebrow && <p className="text-uppercase fw-bold text-warning small mb-1 text-center" style={{ letterSpacing: '.1em' }}>{eyebrow}</p>}
            {title && <h2 className="h3 fw-bold mb-2 text-center">{title}</h2>}
            {intro && <div className="mx-auto text-black-75 mb-4 text-center" style={{ maxWidth: '60ch' }}><HtmlComponent text={intro} /></div>}

            <Row className="g-3 align-items-stretch">
                {tiers.map((tier, index) => {
                    const name = tierValue(tier, 'name');
                    const audience = tierValue(tier, 'audience');
                    const discount = tierValue(tier, 'discount');
                    const ribbon = tierValue(tier, 'ribbon');
                    // Bootstrap variant chosen per tier, applied to the CTA, the ribbon and the price.
                    const color = tierValue(tier, 'color');
                    const badgeText = ['warning', 'light', 'info'].includes(color) ? 'dark' : 'white';
                    const cta = getContentBlockMixedItem(tier, 'cta') || {};
                    return (
                        <Col xs="12" md="4" key={name || index} className="d-flex">
                            <Card className={classnames('w-100 shadow-sm', color && `border-${color} border-2`)}>
                                <Card.Body className="d-flex flex-column p-4">
                                    {ribbon && <Badge bg={color || 'secondary'} text={badgeText} className="align-self-center align-self-lg-start mb-2 px-2 py-1">{ribbon}</Badge>}
									<div className="d-lg-flex justify-content-between">
										<div className="w-100 w-lg-50">
											{name && <h3 className="h5 fw-bold mb-1 text-center text-lg-start">{name}</h3>}
											{audience && <p className="text-black-75 small mb-3 text-center text-lg-start">{audience}</p>}
										</div>
										{discount && <div className={classnames('display-6 fw-bold lh-1 mb-3 w-lg-50', color ? `text-${color}` : 'text-dark', 'text-center', 'text-lg-end')}>{discount}</div>}
									</div>

                                    {tierRows(tier).length > 0 && (
                                        <ul className="list-unstyled border-top mb-4">
                                            {tierRows(tier).map(row => (
                                                <li key={row.id} className="d-flex justify-content-between gap-2 py-2 border-bottom small">
                                                    <span className="text-black-75"><FormattedMessage id={row.id} defaultMessage={row.defaultMessage} /></span>
                                                    <span className="fw-semibold text-end">{row.value}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}

                                    {cta.text && cta.path && (
                                        <Button as={PredictiveLink} to={cta.path} variant={color || 'outline-warning'} className="mt-auto w-100 fw-bold">
                                            {cta.text}
                                        </Button>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    );
                })}
            </Row>

            {footnote && <div className="text-black-75 small mt-3"><HtmlComponent text={footnote} /></div>}
        </Container>
    );
}

PanelPricingTiers.propTypes = {
    panel: PropTypes.object.isRequired,
};

export default PanelPricingTiers;
